import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FollowUpLinkType } from '@app/modules/follow-ups/follow-ups.types';
import { FollowUpsUtilsService } from '@app/modules/follow-ups/services/follow-ups-utils.service';
import { KitsUtilsService } from '@app/modules/kits/kits-utils.service';
import { SampleFields } from '@app/modules/service-data/components/sample/sample-dialog/field-sets/sample-fields.enum';
import {
  getFieldSet,
  redirectToDhl,
  ValidatorsHandlerType,
} from '@app/modules/service-data/service-data.utils';
import { ProductInventoryItemUtilsService } from '@app/modules/service-data/services/product-inventory-item-utils.service';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import { GetProductInventoryItemListAction } from '@app/modules/service-data/store/product-intentory-item.actions';
import { ProductInventoryItemState } from '@app/modules/service-data/store/product-inventory-item.state';
import { GetSampleAction } from '@app/modules/service-data/store/sample.actions';
import { SampleState } from '@app/modules/service-data/store/sample.state';
import {
  ExtendedSample,
  Sample,
} from '@app/modules/service-data/types/sample.types';
import { ExtendedProductInventoryItem } from '@app/modules/service-data/types/service-data.types';
import { ProductInventory } from '@core/core.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { ProductsListState } from '@core/store/products/products.state';
import { RunState } from '@core/store/runs/runs.state';
import { CoreUtilsService } from '@core/utils/core-utils.service';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { Observable, Subject, switchMap } from 'rxjs';
import { filter, first, map, shareReplay, tap } from 'rxjs/operators';
import { DocumentPreviewComponent } from '../../document-preview/product-inventory-item-document-preview/document-preview.component';
import { SampleDetailsDhlWaybillComponent } from '../sample-details-dhl-waybill/sample-details-dhl-waybill.component';

@Component({
  selector: 'app-sample-details',
  imports: [
    AsyncPipe,
    DatePipe,
    TranslateModule,
    NgbTooltip,
    DocumentPreviewComponent,
    SampleDetailsDhlWaybillComponent,
    NgClass,
    DataTableComponent,
    RequirePermissionDirective,
  ],
  templateUrl: './sample-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sample-details.component.scss'],
})
export class SampleDetailsComponent implements OnInit {
  protected followUpsUtils = inject(FollowUpsUtilsService);
  protected kitsUtils = inject(KitsUtilsService);
  protected coreUtilsService = inject(CoreUtilsService);
  protected dialogRef = inject(NgbActiveModal);
  private sampleUtils = inject(SampleUtilsService);
  private productInventoryItemUtils = inject(ProductInventoryItemUtilsService);
  private serviceDataUtilsService = inject(ServiceDataUtilsService);
  private authService = inject(AccountUtilsService);
  private store = inject(Store);

  @Input()
  public sampleId: string;

  protected fieldSet: string[] = [];

  protected sample$: Observable<ExtendedSample>;
  protected previousSample$: Observable<Sample>;
  protected nextSample$: Observable<Sample>;
  protected onShowPdfSubject$ = new Subject<string>();
  protected piisDataTableConfiguration: DataTableConfiguration;
  protected runs$: Observable<string>;
  protected documentPreviewPermission$: Observable<boolean>;

  private piis$: Observable<ExtendedProductInventoryItem[]>;

  ngOnInit() {
    this.documentPreviewPermission$ =
      this.authService.hasRequiredPermission$('waybillInformation');

    this.sample$ = this.store.dispatch(new GetSampleAction(this.sampleId)).pipe(
      switchMap(() =>
        this.store
          .select(SampleState.getSampleById)
          .pipe(map((getById) => getById(this.sampleId)))
      ),
      filter(Boolean),
      switchMap((sample) =>
        this.sampleUtils.getExtendedSample(sample, {
          doctor: true,
          product: true,
          serviceData: { bundles: true },
          partner: true,
          kit: {},
        })
      ),
      first(),
      tap((sample) => {
        this.store
          .select(ProductsListState.getAllChildProducts)
          .pipe(
            map((getAllProducts) => getAllProducts([sample.productId])),
            first()
          )
          .subscribe((products) => {
            this.fieldSet = getFieldSet(
              products.filter(Boolean).map((product) => product.dataInputType),
              ValidatorsHandlerType.SAMPLE
            );
          });
      }),
      shareReplay(1)
    );

    this.previousSample$ = this.sample$.pipe(
      switchMap((sample) =>
        this.store.dispatch(new GetSampleAction(sample?.previousSampleId)).pipe(
          switchMap(() =>
            this.store.select(SampleState.getSampleById).pipe(
              map((getById) => getById(sample.previousSampleId)),
              filter((sample) => !!sample)
            )
          )
        )
      )
    );

    this.nextSample$ = this.sample$.pipe(
      switchMap((sample) =>
        this.store.dispatch(new GetSampleAction(sample.nextSampleId)).pipe(
          switchMap(() =>
            this.store.select(SampleState.getSampleById).pipe(
              map((getById) => getById(sample.nextSampleId)),
              filter((sample) => !!sample)
            )
          )
        )
      )
    );

    this.runs$ = this.sample$.pipe(
      switchMap((sample) =>
        this.store
          .select(RunState.getRunsByKitId)
          .pipe(map((getByKitId) => getByKitId(sample.kitId)))
      ),
      map((runs) => runs.map((run) => run.name).join(', '))
    );

    this.piis$ = this.sample$.pipe(
      switchMap((sample: Sample) =>
        this.store
          .dispatch(
            new GetProductInventoryItemListAction(
              { ids: sample.productInventoryItems },
              null,
              false
            )
          )
          .pipe(map(() => sample))
      ),
      switchMap((sample: Sample) =>
        this.store
          .select(ProductInventoryItemState.getProductInventoryItemsByIds)
          .pipe(map((getByIds) => getByIds(sample.productInventoryItems)))
      ),
      switchMap((piis: ProductInventory[]) =>
        this.productInventoryItemUtils.getExtendedProductInventoryItemList(piis)
      ),
      first()
    );

    this.piisDataTableConfiguration = {
      title: '',
      rows$: this.piis$,
      selectionMode: DataTableSelectionType.NONE,
      actionsLocation: DataTableActionsLocation.RIGHT,
      columns: [
        {
          label: 'Product',
          property: 'product.internalName',
        },
        {
          label: 'Status',
          property: 'lastStatus',
        },
      ],
      singleActions: [
        {
          name: 'Product inventory item details',
          icon: 'fa-light fa-file',
          type: DataTableActionType.BUTTON,
          action: (event, pii: ExtendedProductInventoryItem) => {
            this.productInventoryItemUtils.openDetails(pii.id);
          },
        },
      ],
    };
  }

  protected openServiceDataDetails(serviceDataId: string) {
    this.serviceDataUtilsService.openDetails(serviceDataId);
  }

  protected openSample(sampleId: string) {
    this.sampleUtils.openDetails(sampleId);
  }

  protected editSample(sampleId: string, serviceDataId: string) {
    this.sampleUtils.openEdit(sampleId, serviceDataId);
  }

  protected addFollowUp(sample: ExtendedSample) {
    this.followUpsUtils.openCreateFollowUps(
      [sample.id],
      FollowUpLinkType.Sample,
      [{ id: sample.kitId }]
    );
  }

  protected readonly redirectToDhl = redirectToDhl;
  protected readonly SampleFields = SampleFields;
  protected readonly getPropertyFromServiceInformation =
    this.serviceDataUtilsService.getPropertyFromServiceInformation;
}
